import { AxiosInstance, AxiosResponse } from 'axios'
import { IRead } from '../interfaces/IRead.js'
import { IWrite } from '../interfaces/IWrite.js'
import qs from 'qs'
import { EventSourcePolyfill } from 'event-source-polyfill'

import {
  BaseRequestOptions,
  JsonAPIErrorMessage,
  JsonAPIErrorResponse,
  JSONApiListResponse,
  JSONApiResourceBase,
  JSONApiResponse,
  ListRequestOptions,
} from '../interfaces/JSONApi.js'
import {
  BaseEvents,
  LetsDialEventSource,
  LetsDialEventSourceOptions,
} from '../types/Events.js'
import mitt from 'mitt'
import logdown from 'logdown'
export abstract class BaseAPI<
  ResourceType extends JSONApiResourceBase,
  events extends BaseEvents = BaseEvents,
>
  implements IWrite<ResourceType>, IRead<ResourceType>
{
  public _client: AxiosInstance
  public _eventSourceClient?: typeof EventSourcePolyfill
  public logger = logdown('@letsdial/socket', {
    prefixColor: '#5EEAD4',
  })
  // public logger = console

  constructor(
    axios: AxiosInstance,
    eventSourceClient?: typeof EventSourcePolyfill,
  ) {
    this._client = axios
    this._eventSourceClient = eventSourceClient
  }

  public async eventSourceClient(url: string, config: any): Promise<any> {
    if (typeof window !== 'undefined') {
      const { EventSourcePolyfill } = await import('event-source-polyfill')
      return new EventSourcePolyfill(url, config)
    } else if (this._eventSourceClient) {
      return new this._eventSourceClient(url, config) as EventSourcePolyfill
    } else {
      throw new Error(
        'EventSourcePolyfill is not available in this environment',
      )
    }
  }

  get resource(): string {
    throw new Error('No Resource Declared')
  }

  get hasEvents(): boolean {
    return false
  }

  async post(item: ResourceType): Promise<JSONApiResponse<ResourceType>> {
    const result: AxiosResponse = await this._client.post(`${this.resource}`, {
      data: item,
    })
    return result.data
  }
  async patch(
    id: string | number,
    item: ResourceType,
  ): Promise<JSONApiResponse<ResourceType>> {
    const result: AxiosResponse = await this._client.patch(
      `${this.resource}/${id}`,
      {
        data: item,
      },
    )
    return result.data
  }
  async delete(id: string | number): Promise<boolean> {
    await this._client.delete(`${this.resource}/${id}`)
    return true
  }
  async list(
    options?: ListRequestOptions,
  ): Promise<JSONApiListResponse<ResourceType>> {
    const result: AxiosResponse = await this._client.get(`${this.resource}`, {
      params: options,
    })
    return result.data
  }
  async get(
    id: string | number,
    options?: BaseRequestOptions,
  ): Promise<JSONApiResponse<ResourceType>> {
    const result: AxiosResponse = await this._client.get(
      `${this.resource}/${id}`,
      {
        params: options,
      },
    )
    return result.data as JSONApiResponse<ResourceType>
  }
  async events(
    options?: LetsDialEventSourceOptions,
  ): Promise<LetsDialEventSource<events>> {
    if (!this.hasEvents) {
      throw new Error('Unsupported')
    }
    const params = {
      lastEventId: options?.lastEventId,
      filters: options?.filters,
    }
    const queryString = qs.stringify(params)
    const headers: { [key: string]: string } = {}
    if (options?.lastEventId) {
      headers['Last-Event-Id'] = options?.lastEventId
    }
    const { token } = await import('../api.js')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    // @ts-ignore
    const mittEvents = mitt<events>()
    const url = `${this._client.defaults.baseURL}${this.resource}/events?${queryString}`
    this.logger.info(`SSE Stream ${this.resource}/events connecting`)
    const eventSource = await this.eventSourceClient(url, { headers })
    eventSource.onmessage = (event: MessageEvent) => {
      if (event.lastEventId) {
        const data: any = JSON.parse(event.data)
        console.log(`SSE Stream ${this.resource}/events Event`, data)
        this.logger.info(`SSE Stream ${this.resource}/events Event`, data)
        mittEvents.emit(data.type, data)
      }
    }
    eventSource.onerror = (msg: Event) => {
      this.logger.info(msg)
      console.log(`SSE Stream ${this.resource}/events Event`, msg)
      mittEvents.emit('error', msg)
    }
    eventSource.onopen = () => {
      this.logger.info(`SSE Stream ${this.resource}/events   connected`)
      console.log(`SSE Stream ${this.resource}/events Event`, 'connected')
      mittEvents.emit('connected', true)
    }

    return {
      events: mittEvents,
      eventSource,
      close() {
        eventSource.close()
        mittEvents.all.clear()
      },
    }
  }
}

export class JSONApiError extends Error {
  public errors: JsonAPIErrorMessage[]
  constructor(error: JsonAPIErrorResponse) {
    super(error.errors[0].message)
    this.errors = error.errors // (1)
  }
}
